import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=anon, obj=user
  const [tenant, setTenant] = useState(null);

  const bootstrap = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setTenant(data.tenant);
    } catch {
      setUser(false);
      setTenant(null);
    }
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("sq_token", data.token);
    setUser(data.user);
    setTenant(data.tenant);
    return data.user;
  };

  const setSession = (data) => {
    if (data?.token) localStorage.setItem("sq_token", data.token);
    if (data?.user) setUser(data.user);
    if (data?.tenant) setTenant(data.tenant);
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("sq_token");
    setUser(false); setTenant(null);
  };

  const can = (perm) => {
    if (!user) return false;
    const R = user.role;
    const M = {
      "risk.write": ["Admin", "RiskManager", "RiskOwner"],
      "risk.delete": ["Admin", "RiskManager"],
      "control.write": ["Admin", "RiskManager"],
      "finding.write": ["Admin", "RiskManager", "RiskOwner"],
      "framework.import": ["Admin"],
      "framework.map": ["Admin", "RiskManager"],
      "framework.read": ["Admin", "RiskManager", "RiskOwner", "Approver", "Viewer"],
      "appetite.write": ["Admin"],
      "acceptance.request": ["Admin", "RiskManager", "RiskOwner"],
      "acceptance.approve": ["Admin", "Approver"],
      "tenant.admin": ["Admin"],
      "audit.read": ["Admin", "RiskManager", "Approver"],
    };
    return (M[perm] || []).includes(R);
  };

  return (
    <AuthCtx.Provider value={{ user, tenant, setTenant, login, logout, can, apiError, refresh: bootstrap, setSession }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
